import { IonSpinner } from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import {
  UPLOAD_FANTASY_LEAGUE_IMAGE,
  DELETE_FANTASY_LEAGUE_IMAGE,
} from '../../../../graphql/mutations/fantasyLeagueImage';
import { EditableImage } from '../../../components/photo-picker/EditableImage';
import { LeagueImage } from '../../../components/LeagueImage';
import type {
  UploadFantasyLeagueImageMutation,
  UploadFantasyLeagueImageMutationVariables,
  DeleteFantasyLeagueImageMutation,
  DeleteFantasyLeagueImageMutationVariables,
} from '../../../../types/graphql-generated';

interface LeagueImageEditorProps {
  leagueId: string;
  image?: string | null;
  updatedAt?: string | null;
  name: string;
}

const SIZE = 64;

/**
 * League image with a camera badge for admins. The mutations return the league's
 * `image` and `updatedAt`, so Apollo refreshes every cached view of the league.
 */
export const LeagueImageEditor = ({ leagueId, image, updatedAt, name }: LeagueImageEditorProps) => {
  const [uploadImage, { loading: uploading }] = useMutation<
    UploadFantasyLeagueImageMutation,
    UploadFantasyLeagueImageMutationVariables
  >(UPLOAD_FANTASY_LEAGUE_IMAGE);
  const [deleteImage, { loading: deleting }] = useMutation<
    DeleteFantasyLeagueImageMutation,
    DeleteFantasyLeagueImageMutationVariables
  >(DELETE_FANTASY_LEAGUE_IMAGE);

  return (
    <EditableImage
      loading={uploading || deleting}
      hasImage={!!image}
      onUpload={async (photo) => {
        const result = await uploadImage({
          variables: { fantasyLeagueId: leagueId, imageBase64: photo.base64, contentType: photo.contentType },
        });
        return result.data?.uploadFantasyLeagueImage?.errors;
      }}
      onDelete={async () => {
        const result = await deleteImage({ variables: { fantasyLeagueId: leagueId } });
        return result.data?.deleteFantasyLeagueImage?.errors;
      }}
      modalTitle="Imagen de la liga"
      deleteLabel="Eliminar imagen"
      editLabel="Cambiar imagen de la liga"
      buttonTestId="edit-league-image-button"
      loadingContent={
        <div
          className="flex items-center justify-center bg-white/15 ring-2 ring-white/70"
          style={{ width: SIZE, height: SIZE, borderRadius: '9999px' }}
        >
          <IonSpinner name="crescent" color="light" data-testid="league-image-spinner" />
        </div>
      }
    >
      <LeagueImage
        image={image}
        updatedAt={updatedAt}
        name={name}
        size={SIZE}
        variant="onDark"
        className="ring-2 ring-white/70"
      />
    </EditableImage>
  );
};
